import { useState, useEffect, useCallback } from 'react'
import {
  LayoutTemplate,
  Plus,
  Eye,
  EyeOff,
  Play,
  Square,
  Loader2,
  Search,
  X,
  Save,
  Trash2,
  ChevronLeft,
  Download,
  AppWindow,
  Upload,
  Star,
  ExternalLink,
  Settings,
} from 'lucide-react'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import TemplateGalleryModal from '../../components/TemplateGalleryModal'
import ImportProjectModal from '../../components/ImportProjectModal'
import TemplatesSettingsModal from '../../components/TemplatesSettingsModal'
import {
  listTemplates,
  listMyApps,
  activateTemplate,
  deactivateTemplate,
  promoteToTemplate,
  updateTemplate,
  listTemplateImages,
  uploadTemplateImages,
  removeTemplateImage,
  startApp,
  stopApp,
  getPlatformSettings,
  type App,
  type TemplatePreviewImage,
} from '../../lib/api'

// ---------------------------------------------------------------------------
// Status config
// ---------------------------------------------------------------------------
const statusConfig: Record<string, { color: string; label: string; pulse: boolean }> = {
  running: { color: 'bg-green-500', label: 'Running', pulse: true },
  stopped: { color: 'bg-gray-500', label: 'Stopped', pulse: false },
  starting: { color: 'bg-yellow-500', label: 'Starting', pulse: true },
  error: { color: 'bg-red-500', label: 'Error', pulse: false },
}

// ---------------------------------------------------------------------------
// Admin Templates Page
// ---------------------------------------------------------------------------
export default function AdminTemplates() {
  const [templates, setTemplates] = useState<App[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const [visibilityState, setVisibilityState] = useState<{ visibleToUsers: boolean; testerCount: number } | null>(null)
  const [togglingName, setTogglingName] = useState<string | null>(null)
  const [selectedTemplate, setSelectedTemplate] = useState<App | null>(null)
  const [previewTemplateName, setPreviewTemplateName] = useState<string | null>(null)
  const [toggleSuccess, setToggleSuccess] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all')

  const loadTemplates = useCallback(async () => {
    try {
      const data = await listTemplates()
      setTemplates(data)
    } catch (err) {
      console.error('Failed to load templates:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadTemplates()
  }, [loadTemplates])

  const loadVisibility = useCallback(async () => {
    try {
      const data = await getPlatformSettings('features')
      const settings = (data.settings ?? {}) as Record<string, unknown>
      const visibleToUsers = Boolean(settings.templates_visible_to_users)
      const allowlist = Array.isArray(settings.templates_allowlist_emails)
        ? (settings.templates_allowlist_emails as unknown[]).filter((e) => typeof e === 'string')
        : []
      setVisibilityState({ visibleToUsers, testerCount: allowlist.length })
    } catch {
      setVisibilityState({ visibleToUsers: false, testerCount: 0 })
    }
  }, [])

  useEffect(() => {
    loadVisibility()
  }, [loadVisibility])

  async function handleToggleActive(template: App) {
    setTogglingName(template.name)
    try {
      if (template.template_active) {
        await deactivateTemplate(template.name)
        setToggleSuccess(`"${template.display_name || template.name}" deactivated`)
      } else {
        await activateTemplate(template.name)
        setToggleSuccess(`"${template.display_name || template.name}" activated`)
      }
      await loadTemplates()
      setTimeout(() => setToggleSuccess(null), 2000)
    } catch (err) {
      console.error('Failed to toggle template:', err)
    } finally {
      setTogglingName(null)
    }
  }

  async function handlePromoted() {
    setShowAddModal(false)
    await loadTemplates()
  }

  function handleSelectTemplate(template: App) {
    setSelectedTemplate(template)
  }

  async function handleDetailClose() {
    setSelectedTemplate(null)
    await loadTemplates()
  }

  // Filter templates
  const filteredTemplates = templates.filter((t) => {
    // Text search
    if (search.trim()) {
      const q = search.toLowerCase()
      const name = (t.display_name || t.name).toLowerCase()
      const oneLiner = (t.template_one_liner || '').toLowerCase()
      if (!name.includes(q) && !oneLiner.includes(q)) return false
    }
    // Status filter
    if (statusFilter === 'active' && !t.template_active) return false
    if (statusFilter === 'inactive' && t.template_active) return false
    return true
  })

  // -----------------------------------------------------------------------
  // Detail / Edit Panel
  // -----------------------------------------------------------------------
  if (selectedTemplate) {
    return (
      <TemplateDetailPanel
        template={selectedTemplate}
        onClose={handleDetailClose}
      />
    )
  }

  // -----------------------------------------------------------------------
  // Main grid view
  // -----------------------------------------------------------------------
  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <LayoutTemplate className="w-6 h-6 text-muted-foreground" />
          <h1 className="text-2xl font-bold">Templates</h1>
          {visibilityState && (
            <VisibilityBadge visibleToUsers={visibilityState.visibleToUsers} testerCount={visibilityState.testerCount} />
          )}
          <span className="text-sm text-muted-foreground">
            {templates.length} template{templates.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowSettingsModal(true)}
            className="p-2 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            title="Templates settings"
            aria-label="Templates settings"
          >
            <Settings className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Template
          </button>
        </div>
      </div>

      {/* Toggle success banner */}
      {toggleSuccess && (
        <div className="mb-4 px-4 py-2 rounded-lg bg-green-500/10 text-green-600 dark:text-green-400 text-sm">
          {toggleSuccess}
        </div>
      )}

      {/* Search and filter */}
      {templates.length > 0 && (
        <div className="mb-6 space-y-3">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search templates..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <div className="flex gap-2">
            {(['all', 'active', 'inactive'] as const).map((filter) => (
              <button
                key={filter}
                onClick={() => setStatusFilter(filter)}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                  statusFilter === filter
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary text-muted-foreground hover:text-foreground'
                }`}
              >
                {filter === 'all' ? 'All' : filter === 'active' ? 'Active' : 'Inactive'}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Empty state */}
      {!loading && templates.length === 0 && (
        <div className="text-center py-20">
          <LayoutTemplate className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium mb-2">No templates yet</h3>
          <p className="text-muted-foreground mb-6">
            Promote an existing app to create your first template.
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Template
          </button>
        </div>
      )}

      {/* Template card grid */}
      {!loading && templates.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((t) => {
            const status = statusConfig[t.status] || statusConfig.stopped
            const isToggling = togglingName === t.name

            return (
              <div
                key={t.name}
                className="rounded-lg border border-border bg-card overflow-hidden hover:border-primary/50 transition-colors cursor-pointer group"
                onClick={() => handleSelectTemplate(t)}
              >
                {/* Hero image / placeholder */}
                <div className="aspect-video bg-gradient-to-br from-primary/10 to-primary/5 relative overflow-hidden">
                  {t.template_hero_image ? (
                    <img
                      src={t.template_hero_image}
                      alt={t.display_name || t.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <LayoutTemplate className="w-10 h-10 text-muted-foreground/30" />
                    </div>
                  )}
                </div>

                {/* Card body */}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <LayoutTemplate className="w-3.5 h-3.5 text-primary shrink-0" />
                      <h3 className="font-semibold truncate">
                        {t.display_name || t.name}
                      </h3>
                    </div>
                    {/* Status dot */}
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span
                        className={`w-2 h-2 rounded-full ${status.color} ${status.pulse ? 'animate-pulse' : ''}`}
                        title={status.label}
                      />
                    </div>
                  </div>

                  {t.template_one_liner && (
                    <p className="text-sm text-muted-foreground truncate mb-3">
                      {t.template_one_liner}
                    </p>
                  )}
                  {!t.template_one_liner && (
                    <p className="text-sm text-muted-foreground/50 truncate mb-3 italic">
                      No description
                    </p>
                  )}

                  <div className="flex items-center gap-2">
                    {/* Activate / Deactivate badge */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleToggleActive(t)
                      }}
                      disabled={isToggling}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                        t.template_active
                          ? 'bg-green-500/10 text-green-600 dark:text-green-400 hover:bg-green-500/20'
                          : 'bg-gray-500/10 text-gray-600 dark:text-gray-400 hover:bg-gray-500/20'
                      } disabled:opacity-50`}
                    >
                      {isToggling ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : t.template_active ? (
                        <Eye className="w-3 h-3" />
                      ) : (
                        <EyeOff className="w-3 h-3" />
                      )}
                      {t.template_active ? 'Active' : 'Inactive'}
                    </button>
                    {/* Preview in gallery */}
                    {t.template_active && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setPreviewTemplateName(t.name)
                        }}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full border border-border hover:bg-secondary transition-colors"
                        title="Preview how this template looks in the gallery"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Preview
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Add Template Modal */}
      {showAddModal && (
        <AddTemplateModal
          onClose={() => setShowAddModal(false)}
          onPromoted={handlePromoted}
        />
      )}

      {/* Gallery Preview Modal */}
      {previewTemplateName && (
        <TemplateGalleryModal
          onClose={() => setPreviewTemplateName(null)}
          initialTemplateName={previewTemplateName}
        />
      )}

      {/* Templates Settings Modal */}
      {showSettingsModal && (
        <TemplatesSettingsModal
          onClose={() => setShowSettingsModal(false)}
          onSaved={({ templatesVisibleToUsers, allowlistEmails }) => {
            setVisibilityState({ visibleToUsers: templatesVisibleToUsers, testerCount: allowlistEmails.length })
          }}
        />
      )}
    </div>
  )
}

function VisibilityBadge({ visibleToUsers, testerCount }: { visibleToUsers: boolean; testerCount: number }) {
  if (visibleToUsers) {
    return (
      <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20">
        Enabled
      </span>
    )
  }
  if (testerCount > 0) {
    return (
      <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
        Test access ({testerCount})
      </span>
    )
  }
  return (
    <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20">
      Disabled
    </span>
  )
}

// ---------------------------------------------------------------------------
// Add Template Modal — chooser, then promote or import
// ---------------------------------------------------------------------------
type AddMode = 'choose' | 'promote' | 'import'

function AddTemplateModal({
  onClose,
  onPromoted,
}: {
  onClose: () => void
  onPromoted: () => void
}) {
  const [mode, setMode] = useState<AddMode>('choose')

  // Import flow: app created via ImportProjectModal, then promote to template
  async function handleImported(appName: string) {
    try {
      await promoteToTemplate(appName)
    } catch (err) {
      console.error('Failed to promote imported app:', err)
    }
    onPromoted()
  }

  if (mode === 'import') {
    return (
      <ImportProjectModal
        onClose={onClose}
        onImported={(appName, _importId) => handleImported(appName)}
      />
    )
  }

  if (mode === 'promote') {
    return <PromoteAppModal onClose={onClose} onPromoted={onPromoted} onBack={() => setMode('choose')} />
  }

  // Chooser
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card border border-border rounded-xl w-full max-w-md">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold">Add Template</h2>
            <button onClick={onClose} className="p-1 rounded hover:bg-secondary transition-colors" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => setMode('promote')}
              className="flex flex-col items-center gap-3 p-6 rounded-lg border border-border hover:border-primary/50 hover:bg-secondary/30 transition-all text-center group"
            >
              <AppWindow className="w-8 h-8 text-muted-foreground group-hover:text-primary transition-colors" />
              <div>
                <p className="font-medium text-sm">From Existing App</p>
                <p className="text-xs text-muted-foreground mt-1">Clone an app as a template</p>
              </div>
            </button>
            <button
              onClick={() => setMode('import')}
              className="flex flex-col items-center gap-3 p-6 rounded-lg border border-border hover:border-primary/50 hover:bg-secondary/30 transition-all text-center group"
            >
              <Download className="w-8 h-8 text-muted-foreground group-hover:text-primary transition-colors" />
              <div>
                <p className="font-medium text-sm">Import Project</p>
                <p className="text-xs text-muted-foreground mt-1">Import from URL or repo</p>
              </div>
            </button>
          </div>
          <div className="mt-6">
            <button
              onClick={onClose}
              className="w-full px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Promote App Modal (pick from existing apps)
// ---------------------------------------------------------------------------
function PromoteAppModal({
  onClose,
  onPromoted,
  onBack,
}: {
  onClose: () => void
  onPromoted: () => void
  onBack: () => void
}) {
  const [apps, setApps] = useState<App[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [promoting, setPromoting] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    async function load() {
      try {
        const data = await listMyApps()
        setApps(data.filter((a) => !a.is_template))
      } catch (err) {
        console.error('Failed to load apps:', err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const filtered = apps.filter((a) => {
    const q = search.toLowerCase()
    return (
      a.name.toLowerCase().includes(q) ||
      (a.display_name || '').toLowerCase().includes(q)
    )
  })

  async function handlePromote(appName: string) {
    setPromoting(appName)
    setError(null)
    setSuccess(null)
    try {
      const template = await promoteToTemplate(appName)
      setSuccess(
        `Template "${template.display_name || template.name}" created! You can now edit its metadata and activate it.`
      )
      setTimeout(() => {
        onPromoted()
      }, 1500)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to promote app to template')
    } finally {
      setPromoting(null)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <h2 className="text-lg font-semibold">Pick an Existing App</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-secondary transition-colors" aria-label="Close modal">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 border-b border-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search apps..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            The app will be cloned as a new template. The original app will not be modified.
          </p>
        </div>

        {error && (
          <div className="mx-4 mt-3 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">{error}</div>
        )}
        {success && (
          <div className="mx-4 mt-3 p-3 rounded-lg bg-green-500/10 text-green-600 dark:text-green-400 text-sm">{success}</div>
        )}

        <div className="flex-1 overflow-y-auto p-4 space-y-1">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <p className="text-center text-muted-foreground py-8 text-sm">
              {search ? 'No matching apps found.' : 'No apps available to promote.'}
            </p>
          )}
          {!loading &&
            filtered.map((a) => (
              <div
                key={a.name}
                className="flex items-center justify-between p-3 rounded-lg hover:bg-secondary/50 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate">{a.display_name || a.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{a.name}</p>
                </div>
                <ConfirmDialog
                  trigger={
                    <button
                      disabled={promoting !== null}
                      className="ml-3 shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
                    >
                      {promoting === a.name ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <LayoutTemplate className="w-3 h-3" />
                      )}
                      Promote
                    </button>
                  }
                  title="Promote to Template"
                  description={`This will clone "${a.display_name || a.name}" as a new template. The original app will not be modified.`}
                  confirmLabel="Promote"
                  loadingLabel="Promoting..."
                  onConfirm={() => handlePromote(a.name)}
                />
              </div>
            ))}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Template Detail / Edit Panel
// ---------------------------------------------------------------------------
function TemplateDetailPanel({
  template,
  onClose,
}: {
  template: App
  onClose: () => void
}) {
  const [displayName, setDisplayName] = useState(template.display_name || '')
  const [oneLiner, setOneLiner] = useState(template.template_one_liner || '')
  const [description, setDescription] = useState(template.template_description || '')
  const [heroImage, setHeroImage] = useState(template.template_hero_image || '')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = useState(false)

  const [images, setImages] = useState<TemplatePreviewImage[]>([])
  const [imagesLoading, setImagesLoading] = useState(true)
  const [addingImage, setAddingImage] = useState(false)
  const [uploadingCount, setUploadingCount] = useState(0)

  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [currentStatus, setCurrentStatus] = useState(template.status)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [imageError, setImageError] = useState<string | null>(null)
  const [heroImageBroken, setHeroImageBroken] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const loadImages = useCallback(async () => {
    setImageError(null)
    try {
      const data = await listTemplateImages(template.name)
      setImages(data)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load images'
      setImageError(msg)
      console.error('Failed to load images:', err)
    } finally {
      setImagesLoading(false)
    }
  }, [template.name])

  useEffect(() => {
    loadImages()
  }, [loadImages])

  async function handleSave() {
    setSaving(true)
    setSaveError(null)
    setSaveSuccess(false)
    try {
      await updateTemplate(template.name, {
        display_name: displayName,
        template_one_liner: oneLiner,
        template_description: description,
        template_hero_image: heroImage,
      })
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 2000)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files
    if (!files || files.length === 0) return
    setAddingImage(true)
    setUploadingCount(files.length)
    setUploadError(null)
    try {
      await uploadTemplateImages(template.name, files)
      await loadImages()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to upload image'
      setUploadError(msg)
      console.error('Failed to upload image:', err)
    } finally {
      setAddingImage(false)
      setUploadingCount(0)
      // Reset the input so the same files can be re-selected
      e.target.value = ''
    }
  }

  function handleSetHero(imageUrl: string) {
    setHeroImage(imageUrl)
  }

  async function handleRemoveImage(imageId: number) {
    try {
      await removeTemplateImage(template.name, imageId)
      await loadImages()
    } catch (err) {
      console.error('Failed to remove image:', err)
    }
  }

  async function handleStart() {
    setActionLoading('start')
    setActionError(null)
    try {
      await startApp(template.name)
      setCurrentStatus('starting')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to start template'
      setActionError(msg)
      console.error('Failed to start template:', err)
    } finally {
      setActionLoading(null)
    }
  }

  async function handleStop() {
    setActionLoading('stop')
    setActionError(null)
    try {
      await stopApp(template.name)
      setCurrentStatus('stopped')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to stop template'
      setActionError(msg)
      console.error('Failed to stop template:', err)
    } finally {
      setActionLoading(null)
    }
  }

  async function handleDelete() {
    setDeleting(true)
    try {
      const { deleteTemplate } = await import('../../lib/api')
      await deleteTemplate(template.name)
      onClose()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete template')
      console.error('Failed to delete template:', err)
    } finally {
      setDeleting(false)
    }
  }

  const status = statusConfig[currentStatus] || statusConfig.stopped

  return (
    <div>
      {/* Back button */}
      <button
        onClick={onClose}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to Templates
      </button>

      <div className="flex items-center gap-3 mb-6">
        <LayoutTemplate className="w-6 h-6 text-muted-foreground" />
        <h1 className="text-2xl font-bold">
          {template.display_name || template.name}
        </h1>
        <span
          className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full ${
            template.template_active
              ? 'bg-green-500/10 text-green-600 dark:text-green-400'
              : 'bg-gray-500/10 text-gray-600 dark:text-gray-400'
          }`}
        >
          {template.template_active ? 'Active' : 'Inactive'}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: metadata form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Metadata */}
          <div className="rounded-lg border border-border bg-card p-5">
            <h2 className="text-sm font-semibold uppercase text-muted-foreground mb-4">
              Metadata
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Display Name</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  placeholder="Template display name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">One-liner</label>
                <input
                  type="text"
                  value={oneLiner}
                  onChange={(e) => setOneLiner(e.target.value)}
                  maxLength={200}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  placeholder="Short description for gallery"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                  placeholder="Detailed description for the template detail page"
                />
              </div>
              {heroImage && (
                <div>
                  <label className="block text-sm font-medium mb-1">Hero Image</label>
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-yellow-500" fill="currentColor" />
                    <span className="text-xs text-muted-foreground truncate flex-1">
                      {heroImage.startsWith('data:') ? 'Uploaded image (data URL)' : heroImage}
                    </span>
                  </div>
                  {heroImageBroken ? (
                    <div className="w-full aspect-video rounded-lg border border-destructive/50 bg-destructive/5 flex items-center justify-center">
                      <div className="text-center text-destructive text-xs">
                        <X className="w-5 h-5 mx-auto mb-1" />
                        Image URL is broken
                      </div>
                    </div>
                  ) : (
                    <img
                      src={heroImage}
                      alt="Hero preview"
                      className="w-full aspect-video object-cover rounded-lg border border-border"
                      onError={() => setHeroImageBroken(true)}
                      onLoad={() => setHeroImageBroken(false)}
                    />
                  )}
                </div>
              )}

              {/* Save */}
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 text-sm"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save Metadata
                </button>
                {saveSuccess && (
                  <span className="text-sm text-green-600 dark:text-green-400">
                    Saved!
                  </span>
                )}
                {saveError && (
                  <span className="text-sm text-destructive">{saveError}</span>
                )}
              </div>
            </div>
          </div>

          {/* Preview Images */}
          <div className="rounded-lg border border-border bg-card p-5">
            <h2 className="text-sm font-semibold uppercase text-muted-foreground mb-4">
              Preview Images
            </h2>

            {imagesLoading && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            )}

            {!imagesLoading && images.length === 0 && (
              <p className="text-sm text-muted-foreground mb-4">
                No preview images added yet.
              </p>
            )}

            {!imagesLoading && images.length > 0 && (
              <div className="grid grid-cols-2 gap-3 mb-4">
                {images.map((img) => {
                  const isHero = heroImage === img.image_url
                  return (
                    <div
                      key={img.id}
                      className={`relative group rounded-lg overflow-hidden border-2 transition-colors ${
                        isHero
                          ? 'border-yellow-500'
                          : 'border-border'
                      }`}
                    >
                      <img
                        src={img.image_url}
                        alt="Preview"
                        className="w-full aspect-video object-cover"
                      />
                      {/* Hero badge */}
                      {isHero && (
                        <div className="absolute top-2 left-2 flex items-center gap-1 px-1.5 py-0.5 rounded bg-yellow-500 text-white text-[10px] font-semibold">
                          <Star className="w-3 h-3" fill="currentColor" />
                          Hero
                        </div>
                      )}
                      {/* Action buttons (visible on hover) */}
                      <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {!isHero && (
                          <button
                            onClick={() => handleSetHero(img.image_url)}
                            className="p-1 rounded bg-black/60 text-white hover:bg-yellow-500 transition-colors"
                            title="Set as hero image"
                            aria-label="Set as hero image"
                          >
                            <Star className="w-3 h-3" />
                          </button>
                        )}
                        <button
                          onClick={() => handleRemoveImage(img.id)}
                          className="p-1 rounded bg-black/60 text-white hover:bg-red-600 transition-colors"
                          title="Remove image"
                          aria-label="Remove image"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {imageError && (
              <div className="mb-4 px-3 py-2 rounded-lg bg-destructive/10 text-destructive text-sm">
                {imageError}
              </div>
            )}

            {/* Upload images */}
            <label
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-sm cursor-pointer ${
                addingImage ? 'opacity-50 pointer-events-none' : ''
              }`}
            >
              {addingImage ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading {uploadingCount} image{uploadingCount !== 1 ? 's' : ''}...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Upload Images
                </>
              )}
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileUpload}
                disabled={addingImage}
                className="hidden"
              />
            </label>
            {uploadError && (
              <div className="mt-2 px-3 py-2 rounded-lg bg-destructive/10 text-destructive text-sm">
                {uploadError}
              </div>
            )}
          </div>
        </div>

        {/* Right column: status + actions */}
        <div className="space-y-6">
          {/* Status card */}
          <div className="rounded-lg border border-border bg-card p-5">
            <h2 className="text-sm font-semibold uppercase text-muted-foreground mb-4">
              Status
            </h2>
            <div className="flex items-center gap-2 mb-4">
              <span
                className={`w-2.5 h-2.5 rounded-full ${status.color} ${status.pulse ? 'animate-pulse' : ''}`}
              />
              <span className="text-sm font-medium">{status.label}</span>
            </div>
            <div className="flex gap-2">
              {currentStatus === 'running' || currentStatus === 'starting' ? (
                <button
                  onClick={handleStop}
                  disabled={actionLoading !== null}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border border-border hover:bg-secondary transition-colors disabled:opacity-50"
                >
                  {actionLoading === 'stop' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Square className="w-4 h-4" />
                  )}
                  Stop
                </button>
              ) : (
                <button
                  onClick={handleStart}
                  disabled={actionLoading !== null}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border border-green-500/50 text-green-600 dark:text-green-400 hover:bg-green-500/10 transition-colors disabled:opacity-50"
                >
                  {actionLoading === 'start' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                  Start
                </button>
              )}
            </div>
            {actionError && (
              <div className="mt-3 px-3 py-2 rounded-lg bg-destructive/10 text-destructive text-sm">
                {actionError}
              </div>
            )}
          </div>

          {/* Delete template */}
          <div className="rounded-lg border border-destructive/30 bg-card p-5">
            <h2 className="text-sm font-semibold uppercase text-destructive mb-2">
              Danger Zone
            </h2>
            <p className="text-xs text-muted-foreground mb-3">
              Permanently delete this template. This action cannot be undone.
            </p>
            <ConfirmDialog
              trigger={
                <button
                  disabled={deleting}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border border-destructive/50 text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
                >
                  {deleting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  Delete Template
                </button>
              }
              title="Delete Template"
              description={`Are you sure you want to delete "${template.display_name || template.name}"? This action cannot be undone.`}
              confirmLabel="Delete"
              loadingLabel="Deleting..."
              onConfirm={handleDelete}
              variant="destructive"
            />
          </div>

          {/* Info card */}
          <div className="rounded-lg border border-border bg-card p-5">
            <h2 className="text-sm font-semibold uppercase text-muted-foreground mb-4">
              Info
            </h2>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-muted-foreground">Name</dt>
                <dd className="font-mono text-xs mt-0.5">{template.name}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Visibility</dt>
                <dd className="mt-0.5">
                  {template.template_active ? 'Visible in gallery' : 'Hidden from gallery'}
                </dd>
              </div>
              {template.created_at && (
                <div>
                  <dt className="text-muted-foreground">Created</dt>
                  <dd className="mt-0.5">
                    {new Date(template.created_at).toLocaleDateString()}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      </div>
    </div>
  )
}
